// views/Dashboard/PainelExecutivo/types.ts
import type { PeriodoDashboard, PeriodoFiltro } from '../../../src/lib/dashboard/periodoDashboard';

export type { PeriodoDashboard };

// Definição canônica em src/lib/dashboard/departamentoComercial.ts —
// importada e reexportada aqui (mesmo padrão já usado acima pra
// PeriodoDashboard) pra não mexer nos imports já existentes de
// FiltrosGlobaisDashboard.tsx e SecaoFinanceiro.tsx (Planos 1 e 2), e pro
// tipo continuar utilizável mais abaixo neste mesmo arquivo (em
// FiltrosDashboard e OPCOES_DEPARTAMENTO) — um `export type { X } from
// '...'` sozinho (sem o `import type` companheiro) REEXPORTA X pra quem
// importar deste arquivo, mas não cria um binding local de X usável aqui
// dentro; só o `import type` faz isso. 'empresa_parceira' depende do
// responsável da negociação estar corretamente cadastrado como
// empresa_parceira_admin/operador — limitação conhecida, documentada na spec.
import type { DepartamentoComercial } from '../../../src/lib/dashboard/departamentoComercial';
export type { DepartamentoComercial };
import type { BaseDataFinanceiro } from '../../../src/lib/dashboard/filtrarFaturasPorPeriodo';
export type { BaseDataFinanceiro };
import type { ContagemFunil } from '../../../src/lib/dashboard/funilJornadaNoPeriodo';
export type { ContagemFunil };

/** Uma aba do painel. O valor é o pedaço da URL: /dashboard/painel-executivo/<area>. */
export type AreaPainel = 'ceo' | 'visao-geral' | 'comercial' | 'financeiro' | 'marketing' | 'geradoras' | 'equipes' | 'metas';

/**
 * Raiz do painel. Os links das abas são ABSOLUTOS, montados a partir daqui.
 *
 * ⚠️ Link relativo NÃO serve nesta tela, e isso não é preferência: o painel roda
 * sob uma rota com splat (`painel-executivo/*`), e o React Router resolve
 * destino relativo contra o `pathname` do ÚLTIMO match — que, num splat, inclui
 * o segmento já aberto (`getResolveToMatches`, react-router 7.12). O resultado
 * era a URL empilhando a cada clique:
 * /painel-executivo/comercial/marketing/geradoras, até nenhuma aba abrir.
 */
export const CAMINHO_PAINEL = '/dashboard/painel-executivo';

export const caminhoDaArea = (area: AreaPainel): string =>
  area === 'visao-geral' ? CAMINHO_PAINEL : `${CAMINHO_PAINEL}/${area}`;

export const AREAS: { id: AreaPainel; label: string; descricao: string }[] = [
  // Primeira da lista: é a aba do Igor (pedido de 19/10/2026) — só a ponta,
  // sem filtro nenhum para escolher.
  { id: 'ceo', label: 'CEO', descricao: 'Só a ponta: dinheiro do mês, usinas contra a meta e os KPIs dos departamentos.' },
  { id: 'visao-geral', label: 'Visão geral', descricao: 'Por onde começar e o que tem em cada área.' },
  { id: 'comercial', label: 'Comercial', descricao: 'Funil de vendas por etapa, em negócios e em kWh.' },
  { id: 'financeiro', label: 'Financeiro', descricao: 'Emitido, recebido, inadimplência e a previsão de recebíveis.' },
  { id: 'marketing', label: 'Marketing', descricao: 'Investimento em mídia paga, resultados e custo por resultado.' },
  { id: 'geradoras', label: 'Geradoras', descricao: 'Energia gerada contra a esperada, injetada e saldo das usinas.' },
  { id: 'equipes', label: 'Equipes', descricao: 'Eficiência das equipes de Goiânia e Morrinhos, pessoa a pessoa.' },
  // Lugar único das metas mês a mês (#6496): a tela inicial e os KPIs da aba CEO leem daqui.
  { id: 'metas', label: 'Metas', descricao: 'A meta de cada indicador, mês a mês: o que a tela inicial e os KPIs comparam.' },
];

/**
 * Áreas com período e exportação próprios, que ignoram a barra de filtros do
 * topo. Equipes mede o ritmo de trabalho por dia ("últimos 30 dias"); o filtro
 * do topo é por mês de referência e não serve para essa pergunta.
 *
 * CEO também: cada bloco dela já responde um período fixo (o mês corrente, o
 * último mês faturado), e a pergunta do CEO não é "escolha um período".
 */
export const AREAS_COM_FILTRO_PROPRIO: AreaPainel[] = ['ceo', 'equipes', 'metas'];

export const ehAreaPainel = (v: string | undefined): v is AreaPainel =>
  !!v && AREAS.some((a) => a.id === v);

export interface FiltrosDashboard {
  periodo: PeriodoDashboard;
  // Multi-seleção — permite misturar departamentos (ex.: só Interno +
  // Externo, deixando Parceiros/Empresa Parceira de fora). Lista vazia é
  // tratada como "sem filtro" (mesmo efeito de todos selecionados) em
  // filtrarPorDepartamento, pra nunca deixar o painel vazio por engano.
  departamentos: Exclude<DepartamentoComercial, 'todos'>[];
  // Período B do comparativo — null = comparativo desligado. Quando
  // preenchido, cada seção mostra um bloco extra "Comparativo vs. Período B"
  // (ver ComparativoResumo.tsx).
  periodoComparacao: PeriodoDashboard | null;
  /** Filtro só do Financeiro: qual data da fatura o período recorta. */
  baseDataFinanceiro: BaseDataFinanceiro;
  /**
   * Filtro só do Comercial: 'evento' conta cada etapa pela data dela;
   * 'jornada' só conta a negociação com todas as etapas até a do card dentro do
   * período — ver src/lib/dashboard/funilJornadaNoPeriodo.ts.
   */
  contagemComercial: ContagemFunil;
}

export const FILTROS_DASHBOARD_INICIAIS: FiltrosDashboard = {
  periodo: { tipo: 'preset', preset: 'mesAtual' },
  departamentos: ['comercial', 'parceiros', 'empresa_parceira', 'licitacao'],
  periodoComparacao: null,
  baseDataFinanceiro: 'referencia',
  contagemComercial: 'evento',
};

// Tipagem direta em PeriodoFiltro (não um tipo condicional sobre o union
// PeriodoDashboard): como PeriodoDashboard é um nome concreto, não um type
// parameter genérico, um "extends ... infer" sobre ele não distribui por
// membro — resolveria sempre para `never`. PeriodoFiltro já é exatamente o
// tipo dos valores de preset (ver src/lib/dashboard/periodoDashboard.ts).
export const OPCOES_PERIODO_PRESET: { value: PeriodoFiltro; label: string }[] = [
  { value: 'ultimos12', label: 'Últimos 12 meses' },
  { value: 'ultimos6', label: 'Últimos 6 meses' },
  { value: 'ultimos3', label: 'Últimos 3 meses' },
  { value: 'mesAtual', label: 'Mês atual' },
  { value: 'mesAnterior', label: 'Mês anterior' },
  { value: 'trimestreAtual', label: 'Trimestre atual' },
  { value: 'trimestreAnterior', label: 'Trimestre anterior' },
  { value: 'anoAtual', label: 'Ano atual' },
  { value: 'anoAnterior', label: 'Ano anterior' },
  { value: 'historico', label: 'Todo o histórico' },
];

// "Canal de venda" na tela, `departamento` no código: o nome antigo colidia com
// as abas por área do painel, e o filtro nunca foi sobre área — é a origem da
// venda. Renomear só o rótulo evita mexer em `departamentoComercial.ts` e nas
// funções já testadas.
export const OPCOES_DEPARTAMENTO: { value: DepartamentoComercial; label: string }[] = [
  { value: 'todos', label: 'Todos os canais' },
  // Comercial é um canal só desde 29/10/2026 (#6520): Closer, BDR e Hunter juntos.
  { value: 'comercial', label: 'Comercial' },
  { value: 'parceiros', label: 'Parceiros' },
  { value: 'empresa_parceira', label: 'Empresa Parceira' },
  // Licitação não vem do responsável: sai de `negociacoes.origem`, porque venda
  // pública costuma nascer sem vendedor associado — ver `canalDaNegociacao`.
  { value: 'licitacao', label: 'Licitação' },
];
